import { WEB_TRACKER_POLICY } from "./web_policy"

// Shared localStorage keys owned by the tracker. The OWNER key stays local to
// tracker_controller.js; these are exported because map_controller also reads
// the stored trip/pause state to seed presence heartbeats before the tracker
// controller's query-tracking round-trip lands.
export const TRACKER_TRIP_KEY = "gr8tracks:tripId"
export const TRACKER_FINISH_KEY = "gr8tracks:finishPending"
export const TRACKER_DISCARD_KEY = "gr8tracks:discardPending"
export const TRACKER_PAUSE_KEY = "gr8tracks:tripPaused"
export const TRACKER_STOP_KEY = "gr8tracks:stopPending"
export const TRACKER_LEASE_KEY = "gr8tracks:recordingLease"
export const TRACKER_LEASE_TTL_MS = WEB_TRACKER_POLICY.leaseTtlMs

// What a fresh page load should assume about recording BEFORE the tracker
// controller can re-announce: a stored trip id with no pending terminal intent
// (Save/Discard/finish screens own the claim decision) means the trip is very
// likely still recording, so the first presence beat should claim it rather
// than landing as an off-trip beat (which the server treats as abandonment).
export function trackerStoredTrackingSeed({ tripId = null, stopPending = null, finishPending = null, discardPending = null } = {}) {
  const pendingTerminal = !!(stopPending || finishPending || discardPending)
  const tracking = !!(tripId && !pendingTerminal)
  return { tracking, tripId: tracking ? String(tripId) : null }
}

export function trackerStoredStateOwner({ hasStoredState, storedOwnerId, currentUserId } = {}) {
  if (!hasStoredState) return "none"
  if (storedOwnerId == null || storedOwnerId === "") return "foreign"
  return String(storedOwnerId) === String(currentUserId) ? "current" : "foreign"
}

export function trackerRecordingActive({ watchId = null, flusher = null, visHandler = null, hideHandler = null } = {}) {
  return watchId != null || flusher != null || !!visHandler || !!hideHandler
}

// The stored "stop pending" intent (Save/Discard was on screen when the tab
// closed): parse defensively, returning the controller's pendingStop shape.
export function parsePendingStop(raw) {
  try {
    const stop = JSON.parse(raw || "null")
    if (!stop?.id || !stop?.ended_at) return null
    return { id: String(stop.id), endedAt: stop.ended_at, wasPaused: !!stop.paused }
  } catch {
    return null
  }
}

// The paused flag is stored as the plain trip id so other controllers (map
// presence heartbeats) can tell a paused claim from an actively recording one.
export function trackerPausedForTrip(pausedTripId, tripId) {
  if (pausedTripId == null || pausedTripId === "" || tripId == null || tripId === "") return false
  return String(pausedTripId) === String(tripId)
}

// --- multi-tab recording lease ---------------------------------------------
// One tab may record a trip at a time. The recording tab holds a lease
// { tab, trip, claimedAt, at }: `at` is a heartbeat (freshness), `claimedAt`
// the original claim time (priority — the older claim wins a collision).

export function newTrackerTabId(now = Date.now(), random = Math.random) {
  return `${Number(now).toString(36)}-${Math.floor(random() * 0xffffffff).toString(36)}`
}

export function buildTrackerLease({ tabId, tripId = null, claimedAt = null, at = null } = {}) {
  const heartbeat = at == null || at === "" ? NaN : Number(at)
  const heartbeatAt = Number.isFinite(heartbeat) ? heartbeat : Date.now()
  const claim = claimedAt == null || claimedAt === "" ? NaN : Number(claimedAt)
  return {
    tab: String(tabId),
    trip: tripId != null ? String(tripId) : null,
    claimedAt: Number.isFinite(claim) ? claim : heartbeatAt,
    at: heartbeatAt
  }
}

export function parseTrackerLease(raw) {
  try {
    const lease = JSON.parse(raw || "null")
    if (!lease?.tab) return null
    const at = Number(lease.at)
    if (!Number.isFinite(at)) return null
    const claimedAt = Number(lease.claimedAt)
    return {
      tab: String(lease.tab),
      trip: lease.trip != null ? String(lease.trip) : null,
      claimedAt: Number.isFinite(claimedAt) ? claimedAt : at,
      at
    }
  } catch {
    return null
  }
}

export function trackerLeaseFresh(lease, now = Date.now(), ttlMs = TRACKER_LEASE_TTL_MS) {
  return !!lease && Number.isFinite(lease.at) && now - lease.at < ttlMs
}

// What a tab about to record should do with the stored lease: "claim" (none or
// expired — take it), "self" (already ours), or "foreign" (another live tab
// owns it — do not start recording).
export function trackerLeaseAction({ raw = null, tabId = null, now = Date.now(), ttlMs = TRACKER_LEASE_TTL_MS } = {}) {
  const lease = parseTrackerLease(raw)
  if (!trackerLeaseFresh(lease, now, ttlMs)) return "claim"
  return lease.tab === String(tabId) ? "self" : "foreign"
}

// A recording tab observed a lease write. Yield only when the foreign lease is
// live AND outranks us: the older claim wins, ties break on tab id so exactly
// one side yields.
export function trackerLeaseShouldYield({ raw = null, tabId = null, claimedAt = null, now = Date.now(), ttlMs = TRACKER_LEASE_TTL_MS } = {}) {
  const lease = parseTrackerLease(raw)
  if (!trackerLeaseFresh(lease, now, ttlMs)) return false
  if (lease.tab === String(tabId)) return false
  const mine = Number(claimedAt)
  if (!Number.isFinite(mine)) return true
  if (lease.claimedAt !== mine) return lease.claimedAt < mine
  return lease.tab < String(tabId)
}

// --- in-memory unsent-point mirror ------------------------------------------

export function memoryOnlyMirrorEntries(entries = [], tripId = null) {
  return Array.from(entries || []).filter(entry =>
    entry?.id == null && entry.persisting !== true && entry.point &&
    (tripId == null || entry.trip == null || String(entry.trip) === String(tripId)))
}

export function trackerPendingCount(dbCount = 0, mirrorEntries = [], tripId = null) {
  const count = Number(dbCount)
  const unpersisted = Array.from(mirrorEntries || []).filter(entry =>
    entry?.id == null && entry.point &&
    (tripId == null || entry.trip == null || String(entry.trip) === String(tripId)))
  return (Number.isFinite(count) ? count : 0) + unpersisted.length
}

// Trim the mirror to maxSize. Entries already persisted to IndexedDB (id set)
// are dropped first — they still flush from the outbox. Memory-only entries
// are the sole copy of those points (degraded storage), so they are kept
// newest-first; any we must drop are counted so the UI can warn that points
// are being lost instead of dropping them silently.
export function trimUnsentMirror(entries = [], maxSize) {
  const list = Array.from(entries || [])
  const limit = Math.max(0, Number(maxSize) || 0)
  if (list.length <= limit) return { entries: list, droppedUnsent: 0 }
  let overflow = list.length - limit
  const kept = []
  for (const entry of list) {
    if (overflow > 0 && entry?.id != null) { overflow -= 1; continue }
    kept.push(entry)
  }
  let droppedUnsent = 0
  while (kept.length > limit) {
    kept.shift()
    droppedUnsent += 1
  }
  return { entries: kept, droppedUnsent }
}

export function safeStorageGet(key, storage = globalThis.localStorage) {
  try { return storage?.getItem(key) ?? null } catch { return null }
}

export function safeStorageSet(key, value, storage = globalThis.localStorage) {
  try {
    storage?.setItem(key, value)
    return !!storage
  } catch {
    return false
  }
}

export function safeStorageRemove(key, storage = globalThis.localStorage) {
  try {
    storage?.removeItem(key)
    return !!storage
  } catch {
    return false
  }
}
